/**
 * Centralized popup analytics tracking utilities
 * Handles both RudderStack analytics and logEvent fallback
 */

import { logEvent } from '../analytics/analytics'
import { getSessionId } from '../analytics/eventQueue'

/**
 * Infer page type from pathname
 */
function inferPageFromPathname(pathname: string): 'home' | 'public_restaurant' | 'unknown' {
  if (pathname.includes('/restaurant/')) {
    return 'public_restaurant'
  }
  if (pathname === '/' || pathname === '/home') {
    return 'home'
  }
  return 'unknown'
}

/**
 * Extract restaurant alias from pathname
 * Assumes format: /restaurant/{alias}/details
 */
function extractRestaurantAlias(pathname: string): string | null {
  const match = pathname.match(/\/restaurant\/([^/]+)/)
  return match ? match[1] : null
}

/**
 * Get route without query params
 */
function getRoute(): string {
  return window.location.pathname
}

/**
 * Track popup CTA shown event
 */
export function trackPopupShown(
  params: {
    page?: 'home' | 'public_restaurant' | 'unknown'
    trigger?: 'delay' | 'scroll' | 'manual' | 'unknown'
    modalType: 'web' | 'mobile'
    restaurantAlias?: string | null
  },
  _analytics?: any,
  _isAnalyticsReady?: boolean,
): void {
  const route = getRoute()
  const page = params.page ?? inferPageFromPathname(route)
  const restaurantAlias = params.restaurantAlias ?? extractRestaurantAlias(route)
  const trigger = params.trigger ?? 'unknown'
  const sessionId = getSessionId()

  const eventParams = {
    page,
    route,
    restaurantAlias: restaurantAlias ?? undefined,
    trigger,
    modalType: params.modalType,
    sessionId,
  }

  // Always use logEvent for consistent flushing (handles both RudderStack and gtag)
  logEvent('popup_cta_shown', eventParams)
}

/**
 * Track popup CTA dismissed event
 */
export function trackPopupDismissed(
  params: {
    page?: 'home' | 'public_restaurant' | 'unknown'
    trigger?: 'delay' | 'scroll' | 'manual' | 'unknown'
    modalType: 'web' | 'mobile'
    dismissReason: 'close_button' | 'backdrop' | 'escape' | 'unknown'
    restaurantAlias?: string | null
  },
  _analytics?: any,
  _isAnalyticsReady?: boolean,
): void {
  const route = getRoute()
  const page = params.page ?? inferPageFromPathname(route)
  const restaurantAlias = params.restaurantAlias ?? extractRestaurantAlias(route)
  const trigger = params.trigger ?? 'unknown'
  const sessionId = getSessionId()

  const eventParams = {
    page,
    route,
    restaurantAlias: restaurantAlias ?? undefined,
    trigger,
    modalType: params.modalType,
    dismissReason: params.dismissReason,
    sessionId,
  }

  // Always use logEvent for consistent flushing (handles both RudderStack and gtag)
  logEvent('popup_cta_dismissed', eventParams)
}

/**
 * Track popup CTA clicked event
 */
export function trackPopupClicked(
  params: {
    page?: 'home' | 'public_restaurant' | 'unknown'
    trigger?: 'delay' | 'scroll' | 'manual' | 'unknown'
    modalType: 'web' | 'mobile'
    destination: 'login' | 'points' | 'onboard'
    restaurantAlias?: string | null
  },
  _analytics?: any,
  _isAnalyticsReady?: boolean,
): void {
  const route = getRoute()
  const page = params.page ?? inferPageFromPathname(route)
  const restaurantAlias = params.restaurantAlias ?? extractRestaurantAlias(route)
  const trigger = params.trigger ?? 'unknown'
  const sessionId = getSessionId()

  const eventParams = {
    page,
    route,
    restaurantAlias: restaurantAlias ?? undefined,
    trigger,
    modalType: params.modalType,
    destination: params.destination,
    sessionId,
  }

  // Always use logEvent for consistent flushing (handles both RudderStack and gtag)
  logEvent('popup_cta_clicked', eventParams)
}

